import React, { useState, useEffect } from 'react';
import { useToast } from '../../hooks/useToast';
import { usePatchJobMutation } from '../../store/slices/apiSlice';
import type { JobDatesData, JobLockData } from '../../utils/types';
import { Lock } from 'lucide-react';
import { isJobLocked } from '../../utils/lockHelpers';
import { validateDeliveryDate } from '../../utils/deliveryDateValidation';

// Debounce utility function
const debounce = (func: Function, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

interface InlineDateEditorProps {
  jobId: string;
  field: keyof JobDatesData;
  value: string | null;
  dates: JobDatesData;
  jobStatus?: string;
  productionStatus?: string | null;
  isReadOnly?: boolean;
  onDateUpdate?: (field: keyof JobDatesData, value: string | null) => void;
  lockData?: JobLockData | null;
}

export const InlineDateEditor: React.FC<InlineDateEditorProps> = ({
  jobId,
  field,
  value,
  dates,
  jobStatus,
  productionStatus,
  isReadOnly = false,
  onDateUpdate,
  lockData
}) => {
  const toast = useToast();
  const [patchJob] = usePatchJobMutation();
  const [localValue, setLocalValue] = useState(value || '');
  const datesRef = React.useRef(dates);
  const jobStatusRef = React.useRef(jobStatus);
  const productionStatusRef = React.useRef(productionStatus);
  React.useEffect(() => {
    datesRef.current = dates;
  }, [dates]);
  React.useEffect(() => {
    jobStatusRef.current = jobStatus;
  }, [jobStatus]);
  React.useEffect(() => {
    productionStatusRef.current = productionStatus;
  }, [productionStatus]);
  
  // Lock checking - ONLY for potential_delivery_date
  const locked = field === 'potential_delivery_date' && isJobLocked(lockData);
  const effectiveReadOnly = isReadOnly || locked;  // Lock makes it readonly
  
  // Debug logging
  React.useEffect(() => {
    if (field === 'potential_delivery_date') {
      console.log(`📅 InlineDateEditor [${jobId}] potential_delivery_date:`, {
        lockData,
        isJobLocked: isJobLocked(lockData),
        locked,
        disabled: locked,
        value
      });
    }
  }, [jobId, field, lockData, locked, value]);

  const formatDateForInput = (dateString: string | null) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      // For date input: YYYY-MM-DD
      if (dateString.includes('T') || dateString.includes(' ')) {
        // DateTime input: YYYY-MM-DDTHH:MM
        return date.toISOString().slice(0, 16);
      } else {
        // Date input: YYYY-MM-DD
        return date.toISOString().slice(0, 10);
      }
    } catch {
      return '';
    }
  };

  // Validation logic (extracted from JobDatesManager)
  const validateDates = (
    field: keyof JobDatesData,
    value: string,
    baseDates: JobDatesData,
    js?: string,
    ps?: string | null
  ): string | null => {
    const newDates = { ...baseDates, [field]: value };
    
    // Glass Arrival Date Validation
    if (field === 'glass_arrival_date' && value && newDates.manufacture_date) {
      const glassDate = new Date(value);
      const manufDate = new Date(newDates.manufacture_date);
      
      if (glassDate > manufDate) {
        return 'Glass Arrival date cannot be after Manufacturing date';
      }
    }
    
    // Glass Arrival vs Potential Delivery Date Validation
    if (field === 'glass_arrival_date' && value && newDates.potential_delivery_date) {
      const glassDate = new Date(value);
      const potentialDate = new Date(newDates.potential_delivery_date);
      
      if (glassDate >= potentialDate) {
        return 'Glass Arrival date cannot be same as or after Potential Delivery date';
      }
    }
    
    // Manufacturing Date Validation
    if (field === 'manufacture_date' && value && newDates.glass_arrival_date) {
      const glassDate = new Date(newDates.glass_arrival_date);
      const manufDate = new Date(value);
      
      if (manufDate < glassDate) {
        return 'Manufacturing date cannot be before Glass Arrival date';
      }
    }
    
    // Potential Delivery Date Validation - Use shared validation utility
    if (field === 'potential_delivery_date' && value) {
      const validationError = validateDeliveryDate({
        potentialDeliveryDate: value,
        manufactureDate: newDates.manufacture_date,
        glassArrivalDate: newDates.glass_arrival_date,
        jobStatus: js,
        productionStatus: ps ?? undefined,
      });
      
      if (validationError) {
        return validationError;
      }
    }
    
    return null; // No validation errors
  };

  const handleDateUpdate = async (field: keyof JobDatesData, value: string, revertValue?: string) => {
    const validationError = validateDates(
      field,
      value,
      datesRef.current,
      jobStatusRef.current,
      productionStatusRef.current
    );
    if (validationError) {
      // Clear invalid date to make it easier for user to enter new valid date
      setLocalValue('');
      toast.error('Date Validation Error', validationError);
      return;
    }
    
    try {
      const updateData: Partial<JobDatesData> = {
        [field]: value || null
      };
      
      await patchJob({ 
        id: jobId, 
        data: updateData as any
      }).unwrap();
      
      // Update local state
      setLocalValue(value);
      
      // Notify parent component
      onDateUpdate?.(field, value || null);
      
      const fieldLabels = {
        glass_arrival_date: 'Glass Arrival Date',
        manufacture_date: 'Manufacturing Date', 
        potential_delivery_date: 'Potential Delivery Date'
      };
      
      toast.success(
        'Date Updated', 
        `${fieldLabels[field]} ${value ? 'updated' : 'cleared'} successfully`
      );
      
    } catch (error: any) {
      // Revert to previous value if API call fails
      if (revertValue !== undefined) {
        setLocalValue(revertValue);
      }
      toast.error('Update Failed', error?.data?.detail || 'Please try again');
    }
  };

  const handleDateUpdateRef = React.useRef(handleDateUpdate);
  handleDateUpdateRef.current = handleDateUpdate;

  // Debounced auto-save: stable debounce instance (refs supply latest validation context)
  const debouncedSave = React.useMemo(
    () =>
      debounce((field: keyof JobDatesData, value: string, revertValue?: string) => {
        void handleDateUpdateRef.current(field, value, revertValue);
      }, 400),
    []
  );

  // Auto-save handler for date inputs
  const handleDateChange = (value: string) => {
    // Prevent change if locked
    if (locked) {
      const reason = lockData?.reason || 'No reason provided';
      toast.error(
        'Job is Locked',
        `Cannot modify dates.\nReason: ${reason}`,
        { duration: 4000 }
      );
      return;
    }
    
    const previousValue = localValue;
    
    // Update local state immediately for responsive UI
    setLocalValue(value);
    
    // Auto-save with debounce
    debouncedSave(field, value, previousValue);
  };

  // Update local value when prop changes
  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  // Get input type and formatting based on field
  const inputType = field === 'potential_delivery_date' ? 'datetime-local' : 'date';
  const formattedValue = formatDateForInput(localValue);

  // If readonly (not first order), show as empty/text only
  if (isReadOnly) {
    return (
      <div className="text-sm text-gray-600">
        {value ? new Date(value).toLocaleDateString() : '-'}
      </div>
    );
  }

  // Show input field (disabled if locked) - ONLY for first order
  return (
    <div className="relative">
      {locked && (
        <div className="absolute -top-6 left-0 text-xs text-amber-700 whitespace-nowrap">
          🔒 Locked
        </div>
      )}
      <input
        type={inputType}
        value={formattedValue}
        onChange={(e) => handleDateChange(e.target.value)}
        disabled={locked}
        className="form-input text-xs w-full border-gray-300 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        style={{ minWidth: field === 'potential_delivery_date' ? '160px' : '140px' }}
      />
    </div>
  );
};
